import { useEffect, useRef, useState } from 'react';
import { BsSearch } from 'react-icons/bs';
import { RiCloseCircleFill } from 'react-icons/ri';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { PrimaryTextColor, TextColor } from '../../../app/theme/tokens/colors';
import { InputText } from '../../../shared/ui/Inputs';
import { RedLabel } from '../../../shared/messages/labels';
import {
    UNREGISTERED_PATIENT_MESSAGE,
    displayOrDash,
    formatDocumentOrDash,
    getPatients,
    isUnregisteredPortalHomecarePatient,
} from '../../../services/patients/patientsService';
import { useRechargeStore } from '../store/rechargeStore';
import { formatCPF } from '../../../utils/format';

const AutoCompleteDiv = styled.div`
  position: absolute;
  background-color: white;
  box-sizing: border-box;
  border: solid 1px #efefef;

  width: 100%;
  font-size: 14px;
  border-radius: 10px;
  overflow-y: auto;
  max-height: 300px;
  margin-top: 5px;
  z-index: 2;
`;

const AutoCompleteItem = styled.li`
  box-sizing: border-box;
  padding: 10px;
  color: ${(props) => props.fontColor};
  opacity: ${(props) => (props.isInactive ? 0.7 : 1)};
  &:hover {
    background-color: ${(props) => props.bgColor};
    color: white;
    cursor: pointer;
  }
`;

export const AutoCompleteRechargePatients = ({ style, loading }) => {
    const containerRef = useRef(null);
    const [patients, setPatients] = useState([]);
    const [value, setValue] = useState('');
    const [noResults, setNoResults] = useState(false);
    const [infoMessage, setInfoMessage] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const setSelectedPatient = useRechargeStore(
        (state) => state.setSelectedPatient
    );
    const fetchPatients = useRechargeStore((state) => state.fetchPatients);
    const clearPatientsCache = useRechargeStore((state) => state.clearPatientsCache);
    const setExchangeCylinders = useRechargeStore(
        (state) => state.setExchangeCylinders
    );
    const setPatientInfoMessage = useRechargeStore((state) => state.setPatientInfoMessage);
    const selectedPatient = useRechargeStore((state) => state.selectedPatient);
    const patientsLoading = useRechargeStore((state) => state.patientsLoading);
    const isBusy = !!loading || !!patientsLoading;
    const [searchParams] = useSearchParams();
    const timeoutRef = useRef(null);
    const lastQueryRef = useRef('');
    const cacheRef = useRef(new Map());

    const isInactivePatient = (patient) =>
        patient?.status?.toLowerCase() === 'inativo';

    const normalizeSap = (value) => {
        const digits = String(value ?? '').replace(/\D/g, '');
        const withoutZeros = digits.replace(/^0+/, '');

        return withoutZeros || digits;
    };
    const normalizeCpf = (value) => String(value ?? '').replace(/\D/g, '');

    const firstUsableSap = (...values) => {
        for (const value of values) {
            const sap = normalizeSap(value);

            if (sap.length >= 8) {
                return sap;
            }
        }

        return '';
    };

    const getSapNumber = (patient) =>
        firstUsableSap(
            patient?.cod_cli,
            patient?.codCli,
            patient?.sap,
            patient?.sap_customer,
            patient?.sap_code,
            patient?.sapCode,
            patient?.BP,
            patient?.receiving_code
        );
    const getPatientAutocompleteKey = (patient) => {
        const sap = getSapNumber(patient);

        if (sap) {
            return `sap:${sap}`;
        }

        const cpf = normalizeCpf(patient?.cpf ?? patient?.CPF);
        const hasUsableCpf = cpf && !/^0+$/.test(cpf);
        const name = String(patient?.name ?? patient?.patient ?? '').trim().toUpperCase();

        return hasUsableCpf ? `cpf:${cpf}` : `name:${name}`;
    };
    const formatPatientLabel = (patient) => {
        const sap = getSapNumber(patient);
        const name = displayOrDash(patient?.name ?? patient?.patient);
        const cpf = formatDocumentOrDash(patient?.cpf ?? patient?.CPF, formatCPF, { documentType: 'cpf' });

        return `${name} | CPF: ${cpf}${sap ? ` | ID White Martins: ${sap}` : ''}`;
    };
    useEffect(() => {
        const keepState = searchParams.get('keepState');
        if (!keepState) {
            setSelectedPatient(null);
        }
    }, []);

    useEffect(() => {
        if (isBusy) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            setPatients([]);
            setNoResults(false);
            setInfoMessage('');
            setPatientInfoMessage(null);
            setIsOpen(false);
        }
    }, [isBusy]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const onMouseDown = (event) => {
            if (!containerRef.current) return;
            if (!containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, []);

    const openDropdown = () => {
        if (isBusy) return;
        if (value.trim().length < 3) return;
        if (patients.length === 0 && !noResults) return;
        setIsOpen(true);
    };

    const onChange = (value) => {
        if (isBusy) return;
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setValue(value);
        setIsOpen(true);

        const normalized = (value ?? '').trim();

        // Se o usuário apagou tudo, volta imediatamente para a lista inicial.
        if (normalized === '') {
            lastQueryRef.current = '';
            setPatients([]);
            setNoResults(false);
            setInfoMessage('');
            setPatientInfoMessage(null);
            setIsOpen(false);
            if (selectedPatient !== null) {
                setExchangeCylinders({});
                setSelectedPatient(null);
            }
            fetchPatients(null);
            return;
        }

        const digitsOnly = normalized.replace(/\D/g, '');
        const isNumericQuery =
            digitsOnly.length >= 3 &&
            normalized.replace(/[\d\s./-]/g, '').length === 0;
        const searchQuery = isNumericQuery ? digitsOnly : normalized;

        lastQueryRef.current = searchQuery;

        setNoResults(false);
        setInfoMessage('');
        setPatientInfoMessage(null);

        if (searchQuery.length < 3) {
            setPatients([]);
            setNoResults(false);
            setInfoMessage('');
            setPatientInfoMessage(null);
            setIsOpen(false);
            return;
        }

        timeoutRef.current = setTimeout(async () => {
            const query = searchQuery;

            const cached = cacheRef.current.get(query);
            if (cached && cached.infoMessage !== UNREGISTERED_PATIENT_MESSAGE) {
                if (lastQueryRef.current === query) {
                    setPatients(cached.data);
                    setNoResults(Array.isArray(cached.data) && cached.data.length === 0);
                    setInfoMessage(cached?.infoMessage ?? '');
                    setPatientInfoMessage(cached?.infoMessage || null);
                }
                return;
            }

            try {
                const response = await getPatients(null, query, 0, 20, null);
                const autoRegistered = Number(response?.autoRegisterOracleOnly?.createdCount ?? 0) > 0;
                if (autoRegistered) {
                    cacheRef.current.clear();
                    clearPatientsCache();
                }
                const visiblePatients = (response.data || []).filter((patient) => !isUnregisteredPortalHomecarePatient(patient));
                const deduped = visiblePatients.reduce((acc, idx) => {
                    const currentKey = getPatientAutocompleteKey(idx);
                    const exist = acc.find((patient) => getPatientAutocompleteKey(patient) === currentKey);

                    if (!exist) {
                        acc.push(idx);
                    }
                    return acc;
                }, []);
                const nextInfoMessage =
                    !autoRegistered && response.hasUnregisteredDatalakePatient && deduped.length === 0 ? UNREGISTERED_PATIENT_MESSAGE : '';

                if (!autoRegistered && nextInfoMessage !== UNREGISTERED_PATIENT_MESSAGE) {
                    cacheRef.current.set(query, {
                        data: deduped,
                        infoMessage: nextInfoMessage,
                    });
                } else {
                    cacheRef.current.delete(query);
                }
                if (lastQueryRef.current === query) {
                    setPatients(deduped);
                    setNoResults(deduped.length === 0);
                    setInfoMessage(nextInfoMessage);
                    setPatientInfoMessage(nextInfoMessage || null);
                }
            } catch (e) {
                console.warn('Falha ao buscar pacientes no autocomplete:', e);
                if (lastQueryRef.current === query) {
                    setPatients([]);
                    setNoResults(false);
                    setInfoMessage('');
                    setPatientInfoMessage(null);
                }
            }
        }, 450);
    };
    const selectPatient = async (patient) => {
        if (isBusy) return;
        const patientSap = String(
            getSapNumber(patient) ?? patient?.receiving_code ?? ''
        ).trim();
        const patientCpf = String(patient?.cpf ?? patient?.CPF ?? '').trim();
        const patientCpfDigits = normalizeCpf(patientCpf);
        const hasUsableCpf = patientCpfDigits && !/^0+$/.test(patientCpfDigits);
        const patientName = patient?.name ?? patient?.patient ?? null;
        const patientSearchValue =
            patientSap ||
            (hasUsableCpf ? patientCpfDigits : '') ||
            patientName;

        setExchangeCylinders({});
        setSelectedPatient(patient);
        setValue(formatPatientLabel(patient));
        setPatients([]);
        setNoResults(false);
        setInfoMessage('');
        setPatientInfoMessage(null);
        setIsOpen(false);
        clearPatientsCache();
        const refreshedPatients = await fetchPatients(patientSearchValue);
        const refreshedPatient = refreshedPatients?.data?.find((item) => {
            const itemSap = String(
                getSapNumber(item) ?? item?.receiving_code ?? ''
            ).trim();
            const itemCpf = normalizeCpf(item?.cpf ?? item?.CPF);

            return (
                (patientSap && normalizeSap(itemSap) === normalizeSap(patientSap)) ||
                (patientCpfDigits && itemCpf === patientCpfDigits)
            );
        });

        if (refreshedPatient) {
            setSelectedPatient(refreshedPatient);
        }
    };
    const removeSelectedPatient = () => {
        if (isBusy) return;
        // resetStore();
        setExchangeCylinders({});
        setSelectedPatient(null);
        setValue('');
        setNoResults(false);
        setInfoMessage('');
        setPatientInfoMessage(null);
        setIsOpen(false);
        fetchPatients(null);
    };
    return (
        <div
            ref={containerRef}
            style={{ ...style, position: 'relative', boxSizing: 'border-box' }}
        >
            <BsSearch
                style={{ position: 'absolute', top: '12px', left: '12px' }}
            ></BsSearch>
            {selectedPatient !== null && value !== '' ? (
                <RiCloseCircleFill
                    onClick={isBusy ? undefined : removeSelectedPatient}
                    style={{
                        position: 'absolute',
                        top: '7px',
                        right: '7px',
                        color: PrimaryTextColor,
                        fontSize: '26px',
                        cursor: isBusy ? 'default' : 'pointer',
                        opacity: isBusy ? 0.5 : 1,
                    }}
                ></RiCloseCircleFill>
            ) : (
                <></>
            )}

            <InputText
                placeholder="Pesquisar paciente por nome, CPF ou ID White Martins"
                onChange={(e) => onChange(e.target.value)}
                onFocus={openDropdown}
                onClick={openDropdown}
                value={value}
                disabled={isBusy}
                style={{
                    color: PrimaryTextColor,
                    width: '100%',
                    boxSizing: 'border-box',
                    border: 'solid 1px #c9c9c9',
                    padding: '5px',
                    textIndent: '0px',
                    paddingLeft: '35px',
                    paddingRight: '45px',
                }}
            ></InputText>
            {!patientsLoading &&
                isOpen &&
                (patients.length > 0 || noResults || infoMessage) &&
                value.trim().length >= 3 ? (
                <AutoCompleteDiv>
                    {patients.length > 0 ? (
                        <ul style={{ listStyle: 'none', padding: '0px', margin: '0px' }}>
                            {patients.map((p, idx) => (
                                <AutoCompleteItem
                                    fontColor={PrimaryTextColor}
                                    onClick={() => selectPatient(p)}
                                    bgColor={TextColor}
                                    isInactive={isInactivePatient(p)}
                                    key={idx}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: '8px',
                                        }}
                                    >
                                        <span>{formatPatientLabel(p)}</span>
                                        {isInactivePatient(p) ? (
                                            <RedLabel
                                                style={{
                                                    width: '70px',
                                                    fontSize: '10px',
                                                    textTransform: 'uppercase',
                                                    lineHeight: '18px',
                                                }}
                                            >
                                                Inativo
                                            </RedLabel>
                                        ) : (
                                            <></>
                                        )}
                                    </div>
                                </AutoCompleteItem>
                            ))}
                        </ul>
                    ) : (
                        <div
                            style={{
                                padding: '12px',
                                color: PrimaryTextColor,
                                boxSizing: 'border-box',
                            }}
                        >
                            {infoMessage || 'Nenhum paciente encontrado'}
                        </div>
                    )}
                </AutoCompleteDiv>
            ) : (
                <></>
            )}
        </div>
    );
};
